import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import apiClient from "../api/apiClient";

// Types
export interface Permission {
  id: number;
  name: string;
  description: string;
}

export interface RolePermission {
  id: number;
  role_id: number;
  permission_id: number;
  status: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  total_users?: number;
  permissions: RolePermission[];
}

// Fetch all roles with their permissions
export const useRoles = () => {
  return useQuery<Role[], Error>({
    queryKey: ["roles"],
    queryFn: async () => {
      const response = await apiClient.get<{ status: boolean; data: Role[] }>(
        "/role"
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Fetch specific role
export const useRole = (roleId?: number) => {
  return useQuery({
    queryKey: ["role", roleId],
    queryFn: async () => {
      if (!roleId) return null;
      const response = await apiClient.get<{ data: Role }>(`/role/${roleId}`);
      return response.data.data;
    },
    enabled: !!roleId, // Only run if roleId exists
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Fetch all permissions (names, descriptions, etc.)
export const usePermissions = () => {
  return useQuery<Permission[], Error>({
    queryKey: ["permissions"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Permission[] }>(
        "/permission"
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Toggle a permission for a role

// Create a new role
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newRole: { name: string; description: string }) => {
      await apiClient.post("/role/create", newRole);
    },
    onSuccess: () => {
      message.success("Role created successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => {
      message.error("Failed to create role. Please try again.");
    },
  });
};

// Update an existing role
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      roleId,
      updatedRole,
    }: {
      roleId: number;
      updatedRole: { name: string; description: string };
    }) => {
      await apiClient.put(`/role/${roleId}`, updatedRole);
    },
    onSuccess: () => {
      message.success("Role updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => {
      message.error("Failed to update role. Please try again.");
    },
  });
};

// Delete a role
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: number) => {
      await apiClient.delete(`/role/${roleId}`);
    },
    onSuccess: () => {
      message.success("Role deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => {
      message.error("Failed to delete role. Please try again.");
    },
  });
};
